import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { Search, AlertCircle, TrendingUp, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function SearchAnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/search-analytics`)
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;
  if (!data) return <p className="text-xs text-slate-400 py-4">No search data yet</p>;

  const { total_searches, unique_queries, top_queries, zero_result_queries, volume } = data;

  return (
    <div className="space-y-4" data-testid="search-analytics-panel">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-violet-50 rounded-xl p-3 text-center border border-violet-100">
          <p className="text-lg font-bold text-violet-700">{total_searches}</p>
          <p className="text-[10px] text-violet-500">Total Searches</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
          <p className="text-lg font-bold text-amber-700">{unique_queries}</p>
          <p className="text-[10px] text-amber-500">Unique Queries</p>
        </div>
      </div>

      {/* Volume chart */}
      {volume.length > 0 && (
        <div className="bg-slate-50 rounded-xl p-3">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Search Volume (7 days)</p>
          <div style={{ height: 120 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={volume} margin={{ top: 0, right: 0, left: -25, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#94a3b8" }} tickFormatter={v => v.slice(5)} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 9, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Bar dataKey="searches" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top queries */}
      <div>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-1">Top Queries</p>
        {top_queries.length === 0 ? (
          <p className="text-xs text-slate-400 py-3 text-center">No searches logged yet. Start searching to see data.</p>
        ) : (
          <div className="space-y-1.5">
            {top_queries.map((q, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5 px-2 rounded-lg bg-white border border-slate-100" data-testid={`search-query-${i}`}>
                <Search className="w-3 h-3 text-slate-300 flex-shrink-0" />
                <span className="text-xs font-medium text-slate-700 flex-1 truncate">"{q.query}"</span>
                <span className="text-[10px] text-slate-400 font-semibold">{q.count}x</span>
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${q.avg_results > 0 ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"}`}>
                  {q.avg_results > 0 ? `${q.avg_results} results` : "0 results"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Zero-result queries (supply gaps) */}
      {zero_result_queries.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2 px-1">
            <AlertCircle className="w-3.5 h-3.5 text-red-500" />
            <p className="text-[10px] font-bold text-red-500 uppercase tracking-wider">Supply Gaps (Zero Results)</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 space-y-1.5">
            <p className="text-[10px] text-red-600 mb-2">Users searched for these but found nothing — potential new listings:</p>
            {zero_result_queries.map((q, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-xs font-medium text-red-700">"{q.query}"</span>
                <span className="text-[10px] text-red-500 font-semibold">{q.count}x</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {total_searches === 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
          <Search className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p className="text-xs text-slate-500 font-medium">No searches logged yet</p>
          <p className="text-[10px] text-slate-400 mt-1">Search data will appear here as users use the search bar</p>
        </div>
      )}
    </div>
  );
}
